import React from 'react';
import { ImageSizeType } from 'cloudinary-react';

import DocumentMedia from 'consumer-talent/src/components/MediaRequest/components/UploadMedia/AttachedMediaBlock/DocumentMedia';
import { gqlMediaTypeCodeEnum as MEDIA_TYPE, gqlMedia } from '@minecraft.graphql-types';
import CloudinaryPhoto, { CloudinaryPhotoProps } from '@minecraft.cloudinary-photo';
import { MediaAudio, MediaVideoThumbnail, formatMediaToAudio, formatMediaToVideo } from '@minecraft.media';

/**
 * this component needs to be re-implemented or moved into @minecraft.media
 * otherwise it introduces a dependency cycle b/c minecraft.media imports the carousel from molecules
 * and this is in molecules so media & molecules circularly import each other
 * we want to convert this so that it takes the formatted media props from the formatters in minecraft.media
 * it should not take the raw gqlMedia object or cloudinary props
 */
type PhotoProps = {
  type?: ImageSizeType;
} & CloudinaryPhotoProps;

type Props = {
  media: gqlMedia;
  onClick?: (media: gqlMedia) => void;
  'data-qa-id'?: string;
} & Partial<PhotoProps>;

// Same widths Cloudinary used for video thumbnails (`type="sm"` is the customize-submission card)
const VIDEO_THUMBNAIL_WIDTHS: Record<string, number> = {
  xxsm: 50,
  xsm: 144,
  sm: 256,
  md: 353,
  modal: 560,
  lg: 704,
};

const getVideoThumbnailSize = (type?: string, customSize?: number[]) => {
  if (customSize && customSize.length >= 2) {
    return { width: customSize[0], height: customSize[1] };
  }

  return { width: VIDEO_THUMBNAIL_WIDTHS[type ?? 'md'] ?? VIDEO_THUMBNAIL_WIDTHS.md };
};

// This is intended to be a universal, unopinionated media component that can be used as the foundation for specific uses and layouts.
// Ideally, we'll continue to use this and refactor media component usage throughout the app to use this component. (or decide it's not viable and remove it)
const MediaComponent = ({ media, onClick, 'data-qa-id': qaId, ...rest }: Props) => {
  if (!media) return null;
  const handleClick = () => onClick?.(media);

  switch (media?.mediaType.code as MEDIA_TYPE) {
    case MEDIA_TYPE.AUDIO: {
      const audioProps = formatMediaToAudio({ media });
      return <MediaAudio src={audioProps.src} preload="metadata" />;
    }

    case MEDIA_TYPE.VIDEO: {
      const videoProps = formatMediaToVideo({ media });
      const thumbnailSize = getVideoThumbnailSize(rest.type, rest.customSize);
      return (
        <MediaVideoThumbnail
          poster={videoProps.poster}
          alt={videoProps.name ?? ''}
          width={thumbnailSize.width}
          {...(thumbnailSize.height ? { height: thumbnailSize.height } : {})}
          showPlayIcon
          {...(qaId ? { 'data-qa-id': qaId } : {})}
        />
      );
    }

    case MEDIA_TYPE.PHOTO:
      return (
        <CloudinaryPhoto
          fileKey={media?.fileKey}
          onClick={handleClick}
          mediaStorageStatus={media?.mediaStorageStatus?.code}
          transformation={media?.transformation}
          thumbnail={media?.thumbnail as gqlMedia}
          alt={media?.name}
          showThumbnail
          {...(qaId ? { 'data-qa-id': qaId } : {})}
          {...rest}
        />
      );

    case MEDIA_TYPE.DOCUMENT:
      return <DocumentMedia media={media} {...rest} />;

    default:
      return null;
  }
};

export default MediaComponent;
